<script setup>
// Configuracoes do ENDERECO (valem pra todas as paginas): rastreamento
// (GTM/GA4/Pixel/Clarity), espelho server-side (Meta CAPI) e icone do site.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { toast } from 'dashboard/composables/useToast';
import { useAccount } from 'dashboard/composables/useAccount';
import { useRouter } from 'vue-router';
import LandingPagesAPI from 'dashboard/api/landingPages';
import MetaCapiPixelsAPI from 'dashboard/api/metaCapiPixels';
import SettingsPageLayout from 'dashboard/components-next/Settings/SettingsPageLayout.vue';
import SettingsSectionCard from 'dashboard/components-next/Settings/SettingsSectionCard.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { toSquarePngBase64 } from 'dashboard/helper/imageSquare';
import { useLandingPageSite } from './composables/useLandingPageSite';

const { t } = useI18n();
const { accountScopedRoute } = useAccount();
const router = useRouter();
const { site, isLoading, apiError, fetchSite } = useLandingPageSite();

const CAPI_EVENT_PLACEHOLDER = 'Lead';
// Eventos PADRAO da Meta (otimizacao de campanha funciona melhor com eles);
// "Personalizado" libera o campo livre.
const STANDARD_CAPI_EVENTS = [
  'Lead',
  'CompleteRegistration',
  'Contact',
  'SubmitApplication',
  'Schedule',
  'StartTrial',
  'Subscribe',
  'Purchase',
  'AddToCart',
  'InitiateCheckout',
  'ViewContent',
];
const MAX_FAVICON_BYTES = 200 * 1024;
// Banner de cookies e aviso do formulario (LGPD), por endereco. O modo decide
// o que carrega antes de a pessoa responder; o aviso vale em qualquer modo.
const CONSENT_MODES = ['optout', 'optin', 'none'];
const DEFAULT_CONSENT = {
  mode: 'optout',
  notice: true,
  notice_text: '',
  policy_url: '',
  premarked: false,
  precollect: true,
  reask_days: 180,
};

const isSaving = ref(false);
const capiPixels = ref([]);
const capiHelpOpen = ref(false);
const faviconInput = ref(null);
const form = ref({
  gtm_id: '',
  ga4_id: '',
  meta_pixel_id: '',
  clarity_id: '',
  meta_capi_pixel_id: '',
  meta_capi_event: 'Lead',
  consent: { ...DEFAULT_CONSENT },
});

const isCustomCapiEvent = ref(false);
const onCapiEventSelect = value => {
  if (value === '__custom__') {
    isCustomCapiEvent.value = true;
    form.value.meta_capi_event = '';
  } else {
    isCustomCapiEvent.value = false;
    form.value.meta_capi_event = value;
  }
};

const TRACKERS = [
  { key: 'gtm_id', label: 'Google Tag Manager', ph: 'GTM-XXXXXXX' },
  { key: 'ga4_id', label: 'Google Analytics 4', ph: 'G-XXXXXXXXXX' },
  { key: 'meta_pixel_id', label: 'Meta Pixel', ph: '1234567890' },
  { key: 'clarity_id', label: 'Microsoft Clarity', ph: 'abcdefghij' },
];

const backRoute = computed(() => accountScopedRoute('settings_landing_pages'));
const backToList = () => router.push(backRoute.value);
const goToCapiSettings = () =>
  router.push(accountScopedRoute('settings_meta_capi_pixels'));

const saveSite = async (payload, successKey) => {
  isSaving.value = true;
  try {
    const { data } = await LandingPagesAPI.updateSite(site.value.id, payload);
    site.value = data;
    toast.success(t(successKey));
  } catch (error) {
    toast.error(apiError(error));
  } finally {
    isSaving.value = false;
  }
};

const saveTracking = () =>
  saveSite({ tracking_config: form.value }, 'LANDING_PAGES.TRACKING.SAVED');

const pickFavicon = () => faviconInput.value?.click();

// Recorta no centro e converte pra PNG quadrado: o arquivo publicado se
// chama favicon.png, entao precisa SER png (jpeg renomeado quebra em parte
// dos navegadores) e quadrado (senao o icone sai esticado).
const onFaviconSelected = async event => {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;
  if (file.size > MAX_FAVICON_BYTES) {
    toast.error(t('LANDING_PAGES.IDENTITY.TOO_BIG'));
    return;
  }
  try {
    const base64 = await toSquarePngBase64(file);
    await saveSite(
      { favicon_data: base64, favicon_content_type: 'image/png' },
      'LANDING_PAGES.IDENTITY.SAVED'
    );
  } catch {
    toast.error(t('LANDING_PAGES.IDENTITY.INVALID'));
  }
};

const removeFavicon = () =>
  saveSite(
    { favicon_data: '', favicon_content_type: '' },
    'LANDING_PAGES.IDENTITY.REMOVED'
  );

onMounted(async () => {
  await fetchSite();
  if (!site.value) {
    backToList();
    return;
  }
  form.value = {
    ...form.value,
    ...site.value.tracking_config,
    consent: {
      ...DEFAULT_CONSENT,
      ...(site.value.tracking_config?.consent || {}),
    },
  };
  try {
    isCustomCapiEvent.value =
      !!form.value.meta_capi_event &&
      !STANDARD_CAPI_EVENTS.includes(form.value.meta_capi_event);
    const { data } = await MetaCapiPixelsAPI.get();
    // payload real: { payload: { pixels: [...] } } — o parse antigo dava
    // .filter num objeto, estourava e a lista vinha sempre vazia
    const list = data?.payload?.pixels || data?.payload || data || [];
    capiPixels.value = (Array.isArray(list) ? list : []).filter(p => p.enabled);
  } catch {
    capiPixels.value = [];
  }
});
</script>

<template>
  <SettingsPageLayout
    :title="t('LANDING_PAGES.SITE.SETTINGS')"
    :kicker="t('LANDING_PAGES.EDITOR.KICKER')"
    :back-to="backRoute"
  >
    <template v-if="site" #description>
      <span class="font-mono text-[13px] text-n-slate-11">
        {{ site.hostname }}
      </span>
    </template>
    <!-- Salvar mora no cabecalho (padrao das telas de detalhe) -->
    <template v-if="site" #actions>
      <Button
        :label="t('LANDING_PAGES.TRACKING.SAVE')"
        size="sm"
        :is-loading="isSaving"
        @click="saveTracking"
      />
    </template>
    <div v-if="isLoading" class="flex justify-center py-16">
      <Spinner :size="28" class="text-n-brand" />
    </div>

    <template v-else-if="site">
      <!-- Rastreadores -->
      <SettingsSectionCard
        :title="t('LANDING_PAGES.TRACKING.TITLE')"
        :description="t('LANDING_PAGES.TRACKING.DESCRIPTION')"
      >
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-[40rem]">
          <Input
            v-for="field in TRACKERS"
            :key="field.key"
            v-model="form[field.key]"
            :label="field.label"
            :placeholder="field.ph"
          />
        </div>
        <p class="text-[11.5px] text-n-slate-9 mt-3 mb-0">
          {{ t('LANDING_PAGES.TRACKING.CRM_NOTE') }}
        </p>
      </SettingsSectionCard>

      <!-- Cookies e consentimento (LGPD) -->
      <SettingsSectionCard
        :title="t('LANDING_PAGES.CONSENT.TITLE')"
        :description="t('LANDING_PAGES.CONSENT.DESCRIPTION')"
      >
        <div class="flex flex-col gap-4 max-w-[42rem]">
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('LANDING_PAGES.CONSENT.MODE') }}
            </span>
            <select v-model="form.consent.mode" class="!mb-0 !h-10 rounded-lg">
              <option v-for="mode in CONSENT_MODES" :key="mode" :value="mode">
                {{ t(`LANDING_PAGES.CONSENT.MODES.${mode.toUpperCase()}`) }}
              </option>
            </select>
            <span class="text-[11.5px] text-n-slate-9">
              {{
                t(
                  `LANDING_PAGES.CONSENT.MODES.${form.consent.mode.toUpperCase()}_HINT`
                )
              }}
            </span>
          </label>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              v-model="form.consent.policy_url"
              :label="t('LANDING_PAGES.CONSENT.POLICY_URL')"
              placeholder="https://"
            />
            <Input
              v-model="form.consent.reask_days"
              type="number"
              :label="t('LANDING_PAGES.CONSENT.REASK_DAYS')"
              placeholder="180"
            />
          </div>
          <div class="flex items-center justify-between gap-4 py-1">
            <div class="flex flex-col">
              <span class="text-sm text-n-slate-12">
                {{ t('LANDING_PAGES.CONSENT.NOTICE') }}
              </span>
              <span class="text-[11.5px] text-n-slate-9">
                {{ t('LANDING_PAGES.CONSENT.NOTICE_HINT') }}
              </span>
            </div>
            <Switch v-model="form.consent.notice" />
          </div>
          <TextArea
            v-if="form.consent.notice"
            v-model="form.consent.notice_text"
            :label="t('LANDING_PAGES.CONSENT.NOTICE_TEXT')"
            :placeholder="t('LANDING_PAGES.CONSENT.NOTICE_PLACEHOLDER')"
            :max-length="400"
            show-character-count
          />
          <div class="flex items-center justify-between gap-4 py-1">
            <div class="flex flex-col">
              <span class="text-sm text-n-slate-12">
                {{ t('LANDING_PAGES.CONSENT.PRECOLLECT') }}
              </span>
              <span class="text-[11.5px] text-n-slate-9">
                {{ t('LANDING_PAGES.CONSENT.PRECOLLECT_HINT') }}
              </span>
            </div>
            <Switch v-model="form.consent.precollect" />
          </div>
          <div class="flex items-center justify-between gap-4 py-1">
            <div class="flex flex-col">
              <span class="text-sm text-n-slate-12">
                {{ t('LANDING_PAGES.CONSENT.PREMARKED') }}
              </span>
              <span class="text-[11.5px] text-n-slate-9">
                {{ t('LANDING_PAGES.CONSENT.PREMARKED_HINT') }}
              </span>
            </div>
            <Switch v-model="form.consent.premarked" />
          </div>
          <p class="text-[11.5px] text-n-slate-9 mb-0">
            {{ t('LANDING_PAGES.CONSENT.BRIDGE_NOTE') }}
          </p>
        </div>
      </SettingsSectionCard>

      <!-- Server-side (Meta CAPI) -->
      <SettingsSectionCard
        :title="t('LANDING_PAGES.CAPI.TITLE')"
        :description="t('LANDING_PAGES.CAPI.DESCRIPTION')"
      >
        <div class="flex gap-4 flex-wrap items-end max-w-[40rem] mb-3">
          <label class="flex flex-col gap-1 flex-1 min-w-[220px]">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('LANDING_PAGES.CAPI.PIXEL_LABEL') }}
            </span>
            <select
              v-model="form.meta_capi_pixel_id"
              class="w-full !mb-0 !h-10 rounded-lg"
            >
              <option value="">
                {{ t('LANDING_PAGES.CAPI.PIXEL_NONE') }}
              </option>
              <option
                v-for="pixel in capiPixels"
                :key="pixel.id"
                :value="String(pixel.id)"
              >
                {{ pixel.name }}
              </option>
            </select>
          </label>
          <div class="min-w-[180px]">
            <label class="flex flex-col gap-1">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('LANDING_PAGES.CAPI.EVENT_LABEL') }}
              </span>
              <select
                :value="isCustomCapiEvent ? '__custom__' : form.meta_capi_event"
                class="!mb-0 !h-10 rounded-lg"
                @change="onCapiEventSelect($event.target.value)"
              >
                <option
                  v-for="eventName in STANDARD_CAPI_EVENTS"
                  :key="eventName"
                  :value="eventName"
                >
                  {{ eventName }}
                </option>
                <option value="__custom__">
                  {{ t('LANDING_PAGES.CAPI.EVENT_CUSTOM') }}
                </option>
              </select>
            </label>
            <div v-if="isCustomCapiEvent" class="mt-2">
              <Input
                v-model="form.meta_capi_event"
                :placeholder="CAPI_EVENT_PLACEHOLDER"
              />
            </div>
            <p class="text-[11px] text-n-slate-9 mt-1.5 mb-0">
              {{ t('LANDING_PAGES.CAPI.EVENT_HINT') }}
            </p>
          </div>
        </div>
        <p v-if="!capiPixels.length" class="text-[12px] text-n-slate-10 mb-2">
          {{ t('LANDING_PAGES.CAPI.NO_PIXELS') }}
          <button
            class="text-n-brand hover:underline"
            @click="goToCapiSettings"
          >
            {{ t('LANDING_PAGES.CAPI.GO_TO_PIXELS') }}
          </button>
        </p>
        <div>
          <button
            class="text-[12px] text-n-slate-10 hover:text-n-slate-12 inline-flex items-center gap-1.5"
            @click="capiHelpOpen = !capiHelpOpen"
          >
            <span
              class="size-3.5"
              :class="
                capiHelpOpen
                  ? 'i-lucide-chevron-down'
                  : 'i-lucide-chevron-right'
              "
            />
            {{ t('LANDING_PAGES.CAPI.HELP_TOGGLE') }}
          </button>
        </div>
        <ol
          v-if="capiHelpOpen"
          class="list-decimal pl-8 mt-3 text-[12.5px] text-n-slate-11 leading-relaxed max-w-[42rem] flex flex-col gap-1"
        >
          <li>{{ t('LANDING_PAGES.CAPI.HELP_1') }}</li>
          <li>{{ t('LANDING_PAGES.CAPI.HELP_2') }}</li>
          <li>{{ t('LANDING_PAGES.CAPI.HELP_3') }}</li>
          <li>
            {{ t('LANDING_PAGES.CAPI.HELP_4') }}
            <button
              class="text-n-brand hover:underline"
              @click="goToCapiSettings"
            >
              {{ t('LANDING_PAGES.CAPI.GO_TO_PIXELS') }}
            </button>
          </li>
          <li>{{ t('LANDING_PAGES.CAPI.HELP_5') }}</li>
        </ol>
      </SettingsSectionCard>

      <!-- Icone do site -->
      <SettingsSectionCard
        :title="t('LANDING_PAGES.IDENTITY.TITLE')"
        :description="t('LANDING_PAGES.IDENTITY.DESCRIPTION')"
      >
        <div class="flex items-center gap-4">
          <span
            class="size-14 rounded-xl border border-n-weak bg-n-solid-1 inline-flex items-center justify-center overflow-hidden"
          >
            <img
              v-if="site.has_favicon"
              :src="site.favicon_preview"
              alt=""
              class="size-8 object-contain"
            />
            <span v-else class="i-lucide-image size-6 text-n-slate-9" />
          </span>
          <div class="flex gap-2">
            <Button
              :label="
                site.has_favicon
                  ? t('LANDING_PAGES.IDENTITY.REPLACE')
                  : t('LANDING_PAGES.IDENTITY.UPLOAD')
              "
              size="sm"
              variant="outline"
              :is-loading="isSaving"
              @click="pickFavicon"
            />
            <Button
              v-if="site.has_favicon"
              :label="t('LANDING_PAGES.IDENTITY.REMOVE')"
              size="sm"
              variant="ghost"
              color="ruby"
              @click="removeFavicon"
            />
          </div>
          <input
            ref="faviconInput"
            type="file"
            accept="image/png,image/x-icon,image/jpeg,image/svg+xml"
            class="hidden"
            @change="onFaviconSelected"
          />
        </div>
        <p class="text-[11.5px] text-n-slate-9 mt-3 mb-0">
          {{ t('LANDING_PAGES.IDENTITY.HINT') }}
        </p>
      </SettingsSectionCard>
    </template>
  </SettingsPageLayout>
</template>
